import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from './Layout';
import PlaylistEditor from './PlaylistEditor';
import {
  List, Plus, Trash2, Edit2, Video, Clock, MonitorPlay, Calendar, X,
  Shuffle, Play, Zap, Repeat, SkipForward, Radio, BarChart2, AlertCircle, CalendarClock, Tv2,
  Download, Upload, FileJson, CheckCircle2, ChevronLeft, ChevronRight, CalendarDays
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';

// ── Color palette assigned by index to each playlist ────────────────────────
const PALETTE = [
  { bg: 'bg-accent-purple',  block: 'bg-accent-purple/60 border-accent-purple/70',  dot: 'bg-accent-purple',  text: 'text-accent-purple',  icon: 'bg-accent-purple/20 border-accent-purple/30' },
  { bg: 'bg-accent-indigo',  block: 'bg-accent-indigo/60 border-accent-indigo/70',  dot: 'bg-accent-indigo',  text: 'text-accent-indigo',  icon: 'bg-accent-indigo/20 border-accent-indigo/30' },
  { bg: 'bg-blue-500',       block: 'bg-blue-500/60 border-blue-500/70',             dot: 'bg-blue-500',       text: 'text-blue-400',       icon: 'bg-blue-500/20 border-blue-500/30' },
  { bg: 'bg-teal-500',       block: 'bg-teal-500/60 border-teal-500/70',             dot: 'bg-teal-500',       text: 'text-teal-400',       icon: 'bg-teal-500/20 border-teal-500/30' },
  { bg: 'bg-emerald-500',    block: 'bg-emerald-500/60 border-emerald-500/70',       dot: 'bg-emerald-500',    text: 'text-emerald-400',    icon: 'bg-emerald-500/20 border-emerald-500/30' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
/**
 * Build a visual queue preview with dot markers.
 *
 * Pattern: [content × spotInterval] → [spot × spotBlockSize] → repeat
 *
 * Stress-test verification (sinceSpot counts content videos, spot fires AT count):
 *   interval=3,  block=1 → V V V S  V V V S  ...  (orange at 4th, 8th, 12th dot)
 *   interval=5,  block=1 → V V V V V S  ...       (orange at 6th dot)
 *   interval=6,  block=1 → V V V V V V S  ...     (orange at 7th dot)
 *   interval=10, block=2 → 10×V  S S  ...         (orange at 11th–12th dot)
 *   interval=15, block=1 → effectiveMax forced ≥17 so orange is always visible
 *   interval=20, block=1 → effectiveMax forced ≥22
 *
 * @param {number} videoCount   – content videos in playlist
 * @param {number} spotInterval – every N content videos insert a block
 * @param {number} spotBlockSize – how many spot-video dots per insertion (≥1)
 * @param {number} maxShow      – minimum dots to render (expanded if needed)
 */
function buildQueuePreview(videoCount, spotInterval, spotBlockSize = 1, maxShow = 14, avanceInterval = 0, avanceBlockSize = 1) {
  if (videoCount === 0) return [];
  const minInterval = [spotInterval, avanceInterval].filter(v => v > 0).reduce((a, b) => Math.min(a, b), Infinity);
  const maxBlock = (spotInterval > 0 ? spotBlockSize : 0) + (avanceInterval > 0 ? avanceBlockSize : 0);
  const effectiveMax = minInterval < Infinity
    ? Math.max(maxShow, minInterval + maxBlock + 1)
    : maxShow;
  const result = [];
  let sinceSpot = 0;
  let sinceAvance = 0;
  let total = 0;
  while (result.length < effectiveMax) {
    const isLoop = total >= videoCount;
    result.push({ type: 'video', isLoop });
    total++;
    sinceSpot++;
    sinceAvance++;
    if (spotInterval > 0 && sinceSpot >= spotInterval && result.length < effectiveMax) {
      for (let s = 0; s < spotBlockSize; s++) result.push({ type: 'spot' });
      sinceSpot = 0;
    }
    if (avanceInterval > 0 && sinceAvance >= avanceInterval && result.length < effectiveMax) {
      for (let s = 0; s < avanceBlockSize; s++) result.push({ type: 'avance' });
      sinceAvance = 0;
    }
  }
  return result.slice(0, effectiveMax);
}

function formatDur(seconds) {
  if (!seconds) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function timeToHour(hhmm) {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map(Number);
  return h + m / 60;
}

// ── PlayoutMonitor ─────────────────────────────────────────────────────────
function PlayoutMonitor({ nowPlaying, channel }) {
  if (!nowPlaying?.current) return null;

  const { current, upcoming = [], loopCount = 0, scheduledPlaylists: rawScheduled = [] } = nowPlaying;

  // Compute next scheduled playlist using browser local time
  const nextScheduledPlaylist = (() => {
    if (!rawScheduled.length) return null;
    const now = new Date();
    const dayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
    const curMins = now.getHours() * 60 + now.getMinutes();
    const remainingCurrent = (current.duration || 0) - (current.progress || 0);
    let minSecs = Infinity;
    let result = null;
    for (const pl of rawScheduled) {
      if (!pl.days || !pl.start_time) continue;
      const [sh, sm] = pl.start_time.split(':').map(Number);
      const startMins = sh * 60 + sm;
      for (let d = 0; d < 7; d++) {
        const day = dayNames[(now.getDay() + d) % 7];
        if (!pl.days.includes(day)) continue;
        const minsUntil = d === 0 ? startMins - curMins : d * 1440 - curMins + startMins;
        if (minsUntil <= 0) continue;
        const secsUntil = minsUntil * 60;
        if (secsUntil < minSecs) {
          minSecs = secsUntil;
          const secsAfterCurrent = secsUntil - remainingCurrent;
          let pos = secsAfterCurrent <= 0 ? 0 : -1;
          if (pos < 0) {
            let acc = 0;
            for (let i = 0; i < upcoming.length; i++) {
              acc += upcoming[i].duration || 0;
              if (acc >= secsAfterCurrent) { pos = i + 1; break; }
            }
          }
          if (pos >= 0) {
            const h = Math.floor(secsUntil / 3600);
            const m = Math.floor((secsUntil % 3600) / 60);
            result = { playlist_name: pl.name, seconds_until: secsUntil, transition_at_position: pos, timeLabel: h > 0 ? `en ${h}h ${m}m` : `en ${m}m` };
          }
        }
        break;
      }
    }
    return result;
  })();

  // Group consecutive spot/avance items into a single "block" entry so the UI shows
  // "Spot ×N" / "Avance ×N" instead of N separate identical pills.
  const groupedUpcoming = [];
  for (const item of upcoming) {
    const last = groupedUpcoming[groupedUpcoming.length - 1];
    if (item.is_avance && last?._isAvanceGroup) {
      last._count++;
    } else if (item.is_avance) {
      groupedUpcoming.push({ ...item, _isAvanceGroup: true, _count: 1 });
    } else if (item.is_spot && last?._isSpotGroup) {
      last._count++;
    } else if (item.is_spot) {
      groupedUpcoming.push({ ...item, _isSpotGroup: true, _count: 1 });
    } else {
      groupedUpcoming.push(item);
    }
  }
  const preview = groupedUpcoming.slice(0, 7);

  // Find which grouped index corresponds to transition_at_position in raw upcoming
  const transitionGroupedIdx = (() => {
    if (!nextScheduledPlaylist) return -1;
    const pos = nextScheduledPlaylist.transition_at_position;
    let rawIdx = 0;
    for (let gi = 0; gi < groupedUpcoming.length; gi++) {
      if (rawIdx >= pos) return gi;
      rawIdx += (groupedUpcoming[gi]._isSpotGroup || groupedUpcoming[gi]._isAvanceGroup) ? (groupedUpcoming[gi]._count || 1) : 1;
    }
    return -1;
  })();

  // Count content videos before the next spot/avance block (not total items).
  // "Spot en 2" = 2 more content videos play before the block starts.
  const nextSpotIn = (() => {
    let count = 0;
    for (const item of upcoming) {
      if (item.is_spot || item.is_avance) return count;
      count++;
    }
    return null;
  })();

  return (
    <div className="mb-6 rounded-2xl border border-status-live/30 bg-white dark:bg-[#0f1118] overflow-hidden shadow-lg shadow-status-live/5">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-status-live/10 border-b border-status-live/20">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-status-live rounded-full animate-pulse"></div>
          <span className="text-status-live font-bold text-xs tracking-widest uppercase">En Aire</span>
        </div>
        {channel && (
          <span className="text-gray-500 text-xs">
            Canal: <span className="text-gray-700 dark:text-gray-300 font-medium">{channel.name}</span>
          </span>
        )}
        <div className="ml-auto flex items-center gap-3 text-xs text-gray-500">
          {nextSpotIn !== null && (
            <span className="flex items-center gap-1 text-orange-400">
              <Zap size={11} />
              <span>Spot en <span className="font-bold">{nextSpotIn}</span></span>
            </span>
          )}
          <span className="flex items-center gap-1">
            <Repeat size={11} />
            <span>Loop <span className="text-gray-900 dark:text-white font-bold">{loopCount}</span></span>
          </span>
        </div>
      </div>

      <div className="p-4">
        {/* Current item row */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            current.is_avance
              ? 'bg-sky-500/20 border border-sky-500/30'
              : current.is_spot
                ? 'bg-orange-500/20 border border-orange-500/30'
                : 'bg-accent-purple/20 border border-accent-purple/30'
          }`}>
            {current.is_avance
              ? <Tv2 size={16} className="text-sky-400" />
              : current.is_spot
                ? <Zap size={16} className="text-orange-400" />
                : <Play size={16} className="text-accent-purple" />
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              {current.is_avance && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-500/20 text-sky-400 border border-sky-500/30 uppercase tracking-wide flex-shrink-0">
                  AVANCE
                </span>
              )}
              {current.is_spot && !current.is_avance && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-500/20 text-orange-400 border border-orange-500/30 uppercase tracking-wide flex-shrink-0">
                  SPOT
                </span>
              )}
              <p className="text-gray-900 dark:text-white font-semibold text-sm truncate">{current.title}</p>
            </div>
            {/* Progress bar */}
            <div className="w-full h-1.5 bg-black/10 dark:bg-white/10 rounded-full overflow-hidden mb-1">
              <div
                className={`h-full rounded-full transition-all duration-1000 ${
                  current.is_avance ? 'bg-sky-500' : current.is_spot ? 'bg-orange-500' : 'bg-accent-purple'
                }`}
                style={{ width: `${current.progressPercent || 0}%` }}
              />
            </div>
            <div className="flex justify-between text-[11px] text-gray-400 dark:text-gray-500">
              <span>{formatDur(Math.floor(current.progress || 0))}</span>
              <span>{formatDur(Math.floor(current.duration || 0))}</span>
            </div>
          </div>
        </div>

        {/* Upcoming strip */}
        {preview.length > 0 && (
          <div>
            <p className="text-[10px] text-gray-400 dark:text-gray-600 uppercase tracking-wider mb-2 font-semibold flex items-center gap-1">
              <SkipForward size={10} />
              Próximos
            </p>
            <div className="flex gap-1.5 flex-wrap items-center">
              {preview.map((item, idx) => {
                const showDivider = transitionGroupedIdx >= 0 && idx === transitionGroupedIdx;
                const h = nextScheduledPlaylist ? Math.floor(nextScheduledPlaylist.seconds_until / 3600) : 0;
                const m = nextScheduledPlaylist ? Math.floor((nextScheduledPlaylist.seconds_until % 3600) / 60) : 0;
                const timeLabel = h > 0 ? `${h}h ${m}m` : `${m}m`;
                return (
                  <React.Fragment key={idx}>
                    {showDivider && (
                      <div className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold border bg-amber-500/10 border-amber-500/30 text-amber-400">
                        <CalendarClock size={9} className="flex-shrink-0" />
                        <span className="truncate max-w-[120px]">{nextScheduledPlaylist.playlist_name} · {timeLabel}</span>
                      </div>
                    )}
                    <div
                      title={item._isAvanceGroup
                        ? (item._count > 1 ? `Avances de TV (${item._count})` : 'Avance de TV')
                        : item._isSpotGroup
                          ? (item._count > 1 ? `Bloque de spots (${item._count} videos)` : 'Spot publicitario')
                          : item.title}
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium border max-w-[140px] ${
                        item.is_avance
                          ? 'bg-sky-500/10 border-sky-500/30 text-sky-400'
                          : item.is_spot
                            ? 'bg-orange-500/10 border-orange-500/30 text-orange-400'
                            : 'bg-black/5 dark:bg-white/5 border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {item.is_avance
                        ? <Tv2 size={9} className="flex-shrink-0" />
                        : item.is_spot
                          ? <Zap size={9} className="flex-shrink-0" />
                          : <Video size={9} className="flex-shrink-0 text-gray-500" />}
                      <span className="truncate">
                        {item.is_avance
                          ? (item._count > 1 ? `Avance ×${item._count}` : 'Avance')
                          : item.is_spot
                            ? (item._count > 1 ? `Spot ×${item._count}` : 'Spot')
                            : item.title}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
              {groupedUpcoming.length > 7 && (
                <span className="text-xs text-gray-400 dark:text-gray-600">+{groupedUpcoming.length - 7} más</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── TimelineView ────────────────────────────────────────────────────────────
function TimelineView({ playlists, channels }) {
  const DAY_KEYS   = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
  const DAY_LABELS = ['Lunes','Martes','Miérc','Jueves','Viernes','Sáb','Dom'];
  const HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24];

  const now = new Date();
  const currentHour = now.getHours() + now.getMinutes() / 60;
  const currentDayKey = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][now.getDay()];

  const getChannelName = (channelId) => {
    if (!channelId) return null;
    const ch = channels.find(c => c.id === channelId);
    return ch ? ch.name : null;
  };

  // Build blocks per day
  const dayBlocks = DAY_KEYS.map(day => {
    const blocks = [];
    playlists.forEach((pl, idx) => {
      const s = pl.schedule;
      if (!s || !s.days?.includes(day) || !s.start_time || !s.end_time) return;
      const startH = timeToHour(s.start_time);
      const endH   = timeToHour(s.end_time);
      if (endH <= startH) return;
      blocks.push({
        pl,
        colorIdx: idx % PALETTE.length,
        startPct:  (startH / 24) * 100,
        heightPct: ((endH - startH) / 24) * 100,
        label: `${s.start_time}–${s.end_time}`,
      });
    });
    return blocks;
  });

  const hasAnySchedule = playlists.some(pl => pl.schedule?.days?.length > 0);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#1a1f2e] overflow-hidden">
      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-3 border-b border-white/10 flex-wrap">
        <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Playlists:</span>
        {playlists.map((pl, idx) => (
          <div key={pl.id} className="flex items-center gap-1.5 text-xs text-gray-300">
            <div className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${PALETTE[idx % PALETTE.length].bg}`}></div>
            <span className="truncate max-w-[90px]">{pl.name}</span>
            {pl.is_active && (
              <div className="w-1.5 h-1.5 bg-status-live rounded-full animate-pulse flex-shrink-0" title="Activa"></div>
            )}
          </div>
        ))}
        {!hasAnySchedule && (
          <span className="text-xs text-gray-600 italic">Ninguna playlist tiene horarios configurados</span>
        )}
      </div>

      {/* Grid — scrollable on mobile with hint */}
      <div className="overflow-x-auto -mx-1 px-1">
        <p className="text-[10px] text-gray-500 mb-1 sm:hidden">← Desliza para ver todos los días →</p>
        <div className="min-w-[640px]">
          {/* Day headers */}
          <div className="grid border-b border-white/10" style={{ gridTemplateColumns: '36px repeat(7, 1fr)' }}>
            <div className="py-2"></div>
            {DAY_LABELS.map((label, i) => (
              <div
                key={i}
                className={`py-2.5 text-center text-xs font-semibold ${
                  DAY_KEYS[i] === currentDayKey ? 'text-accent-purple' : 'text-gray-400'
                }`}
              >
                {label}
                {DAY_KEYS[i] === currentDayKey && (
                  <div className="w-1 h-1 bg-accent-purple rounded-full mx-auto mt-0.5"></div>
                )}
              </div>
            ))}
          </div>

          {/* Timeline body */}
          <div className="grid relative" style={{ gridTemplateColumns: '36px repeat(7, 1fr)', height: '420px' }}>
            {/* Hour label column */}
            <div className="relative">
              {HOURS.map(h => (
                <div
                  key={h}
                  className="absolute right-1 text-[9px] text-gray-600 leading-none"
                  style={{ top: `${(h / 24) * 100}%`, transform: 'translateY(-50%)' }}
                >
                  {h < 24 ? `${String(h).padStart(2,'0')}h` : ''}
                </div>
              ))}
            </div>

            {/* Day columns */}
            {DAY_KEYS.map((day, dIdx) => (
              <div
                key={day}
                className={`relative border-l border-white/5 ${DAY_KEYS[dIdx] === currentDayKey ? 'bg-accent-purple/[0.02]' : ''}`}
              >
                {/* Hour grid lines */}
                {HOURS.map(h => (
                  <div
                    key={h}
                    className="absolute left-0 right-0 border-t border-white/[0.06]"
                    style={{ top: `${(h / 24) * 100}%` }}
                  />
                ))}

                {/* Current time indicator */}
                {day === currentDayKey && (
                  <div
                    className="absolute left-0 right-0 z-20 pointer-events-none"
                    style={{ top: `${(currentHour / 24) * 100}%` }}
                  >
                    <div className="h-px bg-red-500/70 w-full"></div>
                    <div className="absolute -top-[3px] -left-[3px] w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                  </div>
                )}

                {/* Schedule blocks */}
                {dayBlocks[dIdx].map((block, bIdx) => {
                  const color = PALETTE[block.colorIdx];
                  return (
                    <div
                      key={bIdx}
                      title={`${block.pl.name}: ${block.label}`}
                      className={`absolute left-0.5 right-0.5 rounded border overflow-hidden cursor-default select-none ${color.block}`}
                      style={{ top: `${block.startPct}%`, height: `${block.heightPct}%`, minHeight: '4px' }}
                    >
                      {block.heightPct > 7 && (
                        <div className="px-1 pt-0.5 overflow-hidden">
                          <p className="text-white/90 text-[9px] font-bold truncate leading-tight">{block.pl.name}</p>
                          {block.heightPct > 12 && (
                            <p className="text-white/60 text-[8px] truncate">{block.label}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PlaylistCalendar ─────────────────────────────────────────────────────────
function PlaylistCalendar({ playlists }) {
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });
  const [selectedDay, setSelectedDay] = useState(null);

  const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const DAY_KEYS_SUN_FIRST = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
  // Headers: Mon→Sun (ISO week)
  const COL_HEADERS = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday   = () => { const t = new Date(); t.setDate(1); setCurrentDate(t); setSelectedDay(new Date()); };

  // Calendar grid — Monday-first
  const firstDayJS  = new Date(year, month, 1).getDay(); // 0=Sun
  const startPad    = (firstDayJS + 6) % 7;              // shift so Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev  = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = startPad - 1; i >= 0; i--)
    cells.push({ day: daysInPrev - i, cur: false, date: new Date(year, month - 1, daysInPrev - i) });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ day: d, cur: true, date: new Date(year, month, d) });
  let next = 1;
  while (cells.length % 7 !== 0)
    cells.push({ day: next++, cur: false, date: new Date(year, month + 1, next - 1) });

  const today = new Date();
  const isToday    = (d) => d.toDateString() === today.toDateString();
  const isSelected = (d) => selectedDay && d.toDateString() === selectedDay.toDateString();

  const plsForDate = (date) => {
    const key = DAY_KEYS_SUN_FIRST[date.getDay()];
    return playlists.filter(pl => pl.schedule?.days?.includes(key));
  };

  const selectedPls = selectedDay ? plsForDate(selectedDay) : [];
  const hasAnySchedule = playlists.some(pl => pl.schedule?.days?.length > 0);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#1a1f2e] overflow-hidden">
      {/* Month navigation */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <button onClick={prevMonth}
          className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 transition-colors">
          <ChevronLeft size={16} className="text-gray-400" />
        </button>
        <div className="flex items-center gap-3">
          <h2 className="text-base font-bold text-white">{MONTH_NAMES[month]} {year}</h2>
          <button onClick={goToday}
            className="text-xs px-2.5 py-1 rounded-lg bg-accent-purple/20 hover:bg-accent-purple/30 text-accent-purple border border-accent-purple/30 transition-colors font-medium">
            Hoy
          </button>
        </div>
        <button onClick={nextMonth}
          className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 transition-colors">
          <ChevronRight size={16} className="text-gray-400" />
        </button>
      </div>

      {!hasAnySchedule && (
        <div className="px-5 py-3 bg-yellow-500/5 border-b border-yellow-500/20">
          <p className="text-xs text-yellow-400/80 flex items-center gap-1.5">
            <AlertCircle size={12} />
            Ninguna playlist tiene horarios configurados — usa el botón <CalendarClock size={11} className="inline" /> en cada tarjeta.
          </p>
        </div>
      )}

      <div className="p-4">
        {/* Legend */}
        {playlists.some(pl => pl.schedule?.days?.length > 0) && (
          <div className="flex flex-wrap gap-2 mb-4">
            {playlists.filter(pl => pl.schedule?.days?.length > 0).map((pl, i) => (
              <div key={pl.id} className="flex items-center gap-1.5 text-xs text-gray-300">
                <div className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${PALETTE[playlists.indexOf(pl) % PALETTE.length].bg}`} />
                <span className="truncate max-w-[100px]">{pl.name}</span>
                {pl.is_active && <div className="w-1.5 h-1.5 bg-status-live rounded-full animate-pulse" title="Activa" />}
              </div>
            ))}
          </div>
        )}

        {/* Day-of-week headers */}
        <div className="overflow-x-auto">
        <div className="min-w-[280px]">
        <div className="grid grid-cols-7 mb-1">
          {COL_HEADERS.map(h => (
            <div key={h} className="text-center text-xs font-semibold text-gray-500 py-1.5">{h}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, i) => {
            const pls = cell.cur ? plsForDate(cell.date) : [];
            const todayCell     = isToday(cell.date);
            const selectedCell  = cell.cur && isSelected(cell.date);
            return (
              <div key={i}
                onClick={() => cell.cur && setSelectedDay(prev =>
                  prev && prev.toDateString() === cell.date.toDateString() ? null : cell.date
                )}
                className={`min-h-[70px] p-1.5 rounded-xl border transition-all duration-150 ${
                  !cell.cur
                    ? 'opacity-25 pointer-events-none border-transparent bg-transparent'
                    : selectedCell
                      ? 'border-accent-purple/70 bg-accent-purple/10 cursor-pointer'
                      : todayCell
                        ? 'border-accent-indigo/40 bg-accent-indigo/5 cursor-pointer'
                        : 'border-white/5 bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04] cursor-pointer'
                }`}>
                {/* Day number */}
                <div className={`text-xs font-bold mb-1 w-5 h-5 rounded-full flex items-center justify-center leading-none ${
                  todayCell ? 'bg-accent-purple text-white' : 'text-gray-300'
                }`}>
                  {cell.day}
                </div>
                {/* Playlist pills */}
                <div className="flex flex-col gap-0.5">
                  {pls.slice(0, 2).map(pl => {
                    const colorIdx = playlists.indexOf(pl) % PALETTE.length;
                    return (
                      <div key={pl.id}
                        className={`text-[8px] font-semibold px-1 py-0.5 rounded truncate border ${PALETTE[colorIdx].block}`}>
                        {pl.name}
                      </div>
                    );
                  })}
                  {pls.length > 2 && (
                    <div className="text-[8px] text-gray-500 px-1">+{pls.length - 2}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>{/* min-w */}
        </div>{/* overflow-x-auto */}

        {/* Selected day panel */}
        {selectedDay && (
          <div className="mt-4 p-4 rounded-xl border border-white/10 bg-white/[0.03]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-white capitalize">
                {selectedDay.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </h3>
              <button onClick={() => setSelectedDay(null)}
                className="w-6 h-6 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
                <X size={12} className="text-gray-400" />
              </button>
            </div>
            {selectedPls.length === 0 ? (
              <p className="text-sm text-gray-500 flex items-center gap-2">
                <CalendarDays size={14} />
                No hay playlists programadas para este día
              </p>
            ) : (
              <div className="space-y-2">
                {selectedPls.map(pl => {
                  const colorIdx = playlists.indexOf(pl) % PALETTE.length;
                  const videoCount = pl.items?.length || 0;
                  return (
                    <div key={pl.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                      <div className={`w-3 h-3 rounded-sm flex-shrink-0 ${PALETTE[colorIdx].bg}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-white truncate">{pl.name}</p>
                          {pl.is_active && (
                            <span className="flex items-center gap-1 text-[10px] font-bold text-status-live">
                              <div className="w-1.5 h-1.5 bg-status-live rounded-full animate-pulse" />
                              LIVE
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                          {pl.schedule?.start_time && (
                            <span className="flex items-center gap-1">
                              <Clock size={10} />
                              {pl.schedule.start_time}–{pl.schedule.end_time}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Video size={10} />
                            {videoCount} {videoCount === 1 ? 'video' : 'videos'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
function Playlists({ user, onLogout }) {
  const { t } = useLanguage();

  // Existing state
  const [playlists, setPlaylists]             = useState([]);
  const [channels, setChannels]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [showModal, setShowModal]             = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [formData, setFormData]               = useState({ name: '', description: '', channel_id: '', shuffle_mode: false });
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingPlaylist, setSchedulingPlaylist] = useState(null);
  const [scheduleData, setScheduleData]       = useState({ days: [], start_time: '08:00', end_time: '18:00', loop: true });

  // New state
  const [activeTab, setActiveTab]   = useState('playlists'); // 'playlists' | 'timeline'
  const [nowPlaying, setNowPlaying] = useState(null);
  const [npChannel, setNpChannel]   = useState(null);
  const [spots, setSpots]           = useState([]);

  // Import state
  const importInputRef              = useRef(null);
  const [showImportResult, setShowImportResult] = useState(null); // {matched, unmatched, titles}

  const daysOfWeek = [
    { key: 'monday',    label: 'Lunes' },
    { key: 'tuesday',   label: 'Martes' },
    { key: 'wednesday', label: 'Miércoles' },
    { key: 'thursday',  label: 'Jueves' },
    { key: 'friday',    label: 'Viernes' },
    { key: 'saturday',  label: 'Sábado' },
    { key: 'sunday',    label: 'Domingo' },
  ];

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchPlaylists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/playlists', { headers: { Authorization: `Bearer ${token}` } });
      setPlaylists(res.data.playlists || []);
    } catch {
      toast.error('Error al cargar playlists');
    }
  }, []);

  const fetchChannels = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/channels', { headers: { Authorization: `Bearer ${token}` } });
      setChannels(res.data.channels || []);
    } catch {
      console.error('Error al cargar canales');
    }
  }, []);

  const fetchNowPlaying = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/dashboard/now-playing', { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data;
      if (data.isPlaylist && data.nowPlaying) {
        setNowPlaying(data.nowPlaying);
        setNpChannel(data.channel || null);
      } else {
        setNowPlaying(null);
        setNpChannel(null);
      }
    } catch {
      setNowPlaying(null);
    }
  }, []);

  const fetchSpots = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/spots/active', { headers: { Authorization: `Bearer ${token}` } });
      setSpots(res.data.spots || []);
    } catch {
      setSpots([]);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchPlaylists(), fetchChannels(), fetchNowPlaying(), fetchSpots()])
      .finally(() => setLoading(false));

    // Poll now-playing every 5 seconds
    const poll = setInterval(fetchNowPlaying, 5000);
    return () => clearInterval(poll);
  }, [fetchPlaylists, fetchChannels, fetchNowPlaying, fetchSpots]);

  // ── CRUD handlers ──────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/playlists', formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Playlist creada');
      setShowModal(false);
      setFormData({ name: '', description: '', channel_id: '', shuffle_mode: false });
      fetchPlaylists();
    } catch {
      toast.error('Error al crear playlist');
    }
  };

  const handleToggleActive = async (playlist) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = !playlist.is_active;
      await axios.put(`/api/playlists/${playlist.id}`, { is_active: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newStatus ? 'Playlist activada' : 'Playlist desactivada');
      fetchPlaylists();
      fetchNowPlaying();
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta playlist?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/playlists/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Playlist eliminada');
      fetchPlaylists();
    } catch {
      toast.error('Error al eliminar playlist');
    }
  };

  const handleExport = async (playlist) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/playlists/${playlist.id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `playlist_${playlist.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Playlist exportada');
    } catch {
      toast.error('Error al exportar playlist');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!importInputRef.current) return;
    importInputRef.current.value = '';
    if (!file) return;

    let parsed;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch {
      toast.error('Archivo JSON inválido');
      return;
    }

    if (!parsed?.playlist?.name) {
      toast.error('Formato de archivo no reconocido');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post('/api/playlists/import',
        { playlist: parsed.playlist },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPlaylists();
      setShowImportResult({
        matched: data.matched_count,
        unmatched: data.unmatched_count,
        titles: data.unmatched_titles || [],
        name: parsed.playlist.name
      });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error al importar playlist');
    }
  };

  const openScheduleModal = (playlist) => {
    setSchedulingPlaylist(playlist);
    setScheduleData(
      playlist.schedule
        ? { days: playlist.schedule.days || [], start_time: playlist.schedule.start_time || '08:00', end_time: playlist.schedule.end_time || '18:00', loop: playlist.schedule.loop !== false }
        : { days: [], start_time: '08:00', end_time: '18:00', loop: true }
    );
    setShowScheduleModal(true);
  };

  const handleSaveSchedule = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/playlists/${schedulingPlaylist.id}`, { schedule: scheduleData }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Programación guardada correctamente');
      setShowScheduleModal(false);
      setSchedulingPlaylist(null);
      fetchPlaylists();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar programación');
    }
  };

  const toggleDay = (dayKey) => {
    setScheduleData(prev => ({
      ...prev,
      days: prev.days.includes(dayKey) ? prev.days.filter(d => d !== dayKey) : [...prev.days, dayKey],
    }));
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getChannelName = (channelId) => {
    if (!channelId) return 'Sin canal asignado';
    const ch = channels.find(c => c.id === channelId);
    return ch ? ch.name : 'Canal desconocido';
  };

  const formatScheduleDisplay = (schedule) => {
    if (!schedule?.days?.length) return 'Sin programación';
    const labels = schedule.days.map(d => {
      const day = daysOfWeek.find(dw => dw.key === d);
      return day ? day.label.substring(0, 3) : d;
    }).join(', ');
    return `${labels} ${schedule.start_time}–${schedule.end_time}`;
  };

  // Separate spots from avances (avances come from /api/spots/active with _is_avance flag)
  const pureSpots = spots.filter(s => !s._is_avance);
  const activeAvances = spots.filter(s => s._is_avance);

  // Minimum spot interval among active spots (most frequent).
  // Guard: filter(v > 0) may produce an empty array → Math.min() = Infinity; clamp to 0.
  const allSpotIntervals = pureSpots.map(s => s.interval_videos).filter(v => v > 0);
  const globalSpotInterval = allSpotIntervals.length > 0 ? Math.min(...allSpotIntervals) : 0;

  // Minimum avance interval among active avances.
  const allAvanceIntervals = activeAvances.map(a => a.interval_videos).filter(v => v > 0);
  const globalAvanceInterval = allAvanceIntervals.length > 0 ? Math.min(...allAvanceIntervals) : 0;

  // Total spot-video count per block insertion at the minimum interval.
  // Only spots whose interval matches globalSpotInterval appear at every insertion.
  // Each spot contributes 1 video (legacy) or N videos (multi-video spot via video_files).
  const spotBlockSize = (() => {
    if (globalSpotInterval === 0) return 1;
    return pureSpots
      .filter(s => (s.interval_videos || 5) === globalSpotInterval)
      .reduce((sum, s) => {
        let count = 1;
        try {
          const p = JSON.parse(s.video_files || '[]');
          if (Array.isArray(p) && p.length > 0) count = p.length;
        } catch {}
        return sum + count;
      }, 0) || 1;
  })();

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex justify-center items-center h-screen">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-accent-purple/30 border-t-accent-purple rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-accent-indigo rounded-full animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Playlist editor ────────────────────────────────────────────────────────
  if (editingPlaylist) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-2.5 max-w-[1600px] mx-auto">
          <PlaylistEditor
            playlistId={editingPlaylist}
            onBack={() => { setEditingPlaylist(null); fetchPlaylists(); }}
            user={user}
          />
        </div>
      </Layout>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-2.5 min-h-screen max-w-[1600px] mx-auto">

        {/* Page header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-white">
              Playout 24/7
            </h1>
            <p className="text-gray-400 text-base flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-accent-purple rounded-full animate-pulse"></span>
              Sistema de playout profesional con spots y programación
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={importInputRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={handleImportFile}
            />
            <button
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-sky-600/15 hover:bg-sky-600/25 text-sky-400 border border-sky-600/30 hover:border-sky-600/50 transition-all duration-200"
              onClick={() => importInputRef.current?.click()}
              title="Importar playlist desde JSON"
            >
              <Upload size={16} />
              Importar
            </button>
            <button
              className="btn-primary-gradient flex items-center gap-3"
              onClick={() => setShowModal(true)}
            >
              <Plus size={20} />
              Nueva Playlist
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Playlists', value: playlists.length, icon: <List size={16} />, color: 'text-accent-purple' },
            { label: 'Activas', value: playlists.filter(p => p.is_active).length, icon: <Radio size={16} />, color: 'text-status-live' },
            { label: 'Spots activos', value: spots.length, icon: <Zap size={16} />, color: 'text-orange-400' },
            { label: 'En aire', value: nowPlaying ? '✓' : '—', icon: <BarChart2 size={16} />, color: nowPlaying ? 'text-status-live' : 'text-gray-500' },
          ].map(({ label, value, icon, color }) => (
            <div key={label} className="rounded-xl border border-white/10 bg-[#1a1f2e] px-4 py-3 flex items-center gap-3">
              <div className={`${color}`}>{icon}</div>
              <div>
                <p className={`text-lg font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Live Playout Monitor */}
        {nowPlaying && (
          <PlayoutMonitor nowPlaying={nowPlaying} channel={npChannel} />
        )}

        {/* Tab switcher */}
        <div className="flex items-center gap-1 mb-5 p-1 rounded-xl bg-white/5 border border-white/10 w-fit">
          {[
            { key: 'playlists', label: 'Playlists',  icon: <List size={15} /> },
            { key: 'timeline',  label: 'Timeline',   icon: <Calendar size={15} /> },
            { key: 'calendar',  label: 'Calendario', icon: <CalendarDays size={15} /> },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeTab === tab.key
                  ? 'bg-gradient-to-r from-accent-purple to-accent-indigo text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── PLAYLISTS TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'playlists' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {playlists.map((playlist, plIdx) => {
              const color = PALETTE[plIdx % PALETTE.length];
              const videoCount = playlist.items?.length || 0;
              const queueDots = buildQueuePreview(videoCount, globalSpotInterval, spotBlockSize, 14, globalAvanceInterval, 1);

              return (
                <div
                  key={playlist.id}
                  className={`relative overflow-hidden transition-all duration-300 rounded-2xl border p-5 ${
                    playlist.is_active
                      ? 'bg-[#1a1f2e] border-status-live/40'
                      : 'bg-[#1a1f2e] border-white/10'
                  }`}
                >
                  {playlist.is_active ? (
                    <div className="absolute inset-0 bg-gradient-to-br from-status-live/5 to-transparent pointer-events-none rounded-2xl"></div>
                  ) : null}

                  {/* Header */}
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border ${color.icon}`}>
                      <List size={22} className={color.text} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-white truncate leading-tight">{playlist.name}</h3>
                      <p className="text-gray-500 text-xs truncate mt-0.5">{playlist.description || 'Sin descripción'}</p>
                    </div>
                    {playlist.is_active ? (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-status-live/15 text-status-live border border-status-live/30 flex-shrink-0">
                        <div className="w-1.5 h-1.5 bg-status-live rounded-full animate-pulse"></div>
                        LIVE
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-500/15 text-gray-500 border border-gray-500/30 flex-shrink-0">
                        <div className="w-1.5 h-1.5 bg-gray-500 rounded-full"></div>
                        OFF
                      </span>
                    )}
                  </div>

                  {/* Stats chips */}
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-gray-300">
                      <Video size={12} className="text-gray-500" />
                      {videoCount} Videos
                    </span>
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-gray-300">
                      <Clock size={12} className="text-gray-500" />
                      {formatDur(playlist.total_duration)}
                    </span>
                    {globalSpotInterval > 0 && (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 border border-orange-500/30 text-xs text-orange-400">
                        <Zap size={12} />
                        Spot c/{globalSpotInterval}{spotBlockSize > 1 ? ` ×${spotBlockSize}` : ''}
                      </span>
                    )}
                    {globalAvanceInterval > 0 && (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-500/10 border border-sky-500/30 text-xs text-sky-400">
                        <Tv2 size={12} />
                        Avance c/{globalAvanceInterval}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-gray-300">
                      <Shuffle size={12} className="text-gray-500" />
                      {playlist.shuffle_mode ? 'Aleatorio' : 'En bucle'}
                    </span>
                  </div>

                  {/* Queue preview dots */}
                  {queueDots.length > 0 && (
                    <div className="mb-3 px-3 py-2.5 rounded-lg bg-black/20 border border-white/5">
                      <p className="text-[10px] text-gray-600 uppercase tracking-wider mb-2 font-semibold">Cola de reproducción</p>
                      <div className="flex items-center gap-1 flex-wrap">
                        {queueDots.map((dot, i) => (
                          <div
                            key={i}
                            title={dot.type === 'avance' ? 'Avance de TV' : dot.type === 'spot' ? 'Spot publicitario' : dot.isLoop ? 'Repetición en bucle' : 'Video de contenido'}
                            className={`rounded-full transition-all ${
                              dot.type === 'avance'
                                ? 'w-2.5 h-2.5 bg-sky-500 ring-1 ring-sky-400/50'
                                : dot.type === 'spot'
                                  ? 'w-2.5 h-2.5 bg-orange-500 ring-1 ring-orange-400/50'
                                  : dot.isLoop
                                    ? 'w-2 h-2 bg-gray-600'
                                    : `w-2 h-2 ${color.dot}`
                            }`}
                          />
                        ))}
                        {videoCount > 14 && (
                          <span className="text-[10px] text-gray-600 ml-1">+{videoCount - 14}</span>
                        )}
                      </div>
                      {queueDots.length > 0 && (
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="flex items-center gap-1 text-[10px] text-gray-600">
                            <div className={`w-1.5 h-1.5 rounded-full ${color.dot}`}></div> Contenido
                          </span>
                          {globalSpotInterval > 0 && (
                            <span className="flex items-center gap-1 text-[10px] text-gray-600">
                              <div className="w-2 h-2 rounded-full bg-orange-500"></div> Spot
                            </span>
                          )}
                          {globalAvanceInterval > 0 && (
                            <span className="flex items-center gap-1 text-[10px] text-gray-600">
                              <div className="w-2 h-2 rounded-full bg-sky-500"></div> Avance
                            </span>
                          )}
                          {videoCount > 1 && (
                            <span className="flex items-center gap-1 text-[10px] text-gray-600">
                              <div className="w-1.5 h-1.5 rounded-full bg-gray-600"></div> Bucle
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Schedule box */}
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-gray-400 mb-4">
                    <Calendar size={12} className="text-gray-600 flex-shrink-0" />
                    <span className="truncate">{formatScheduleDisplay(playlist.schedule)}</span>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 mb-2">
                    <button
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ${
                        playlist.is_active
                          ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/25'
                          : 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/25'
                      }`}
                      onClick={() => handleToggleActive(playlist)}
                    >
                      <MonitorPlay size={15} />
                      {playlist.is_active ? 'Detener' : 'Activar'}
                    </button>
                    <button
                      className={`w-10 h-10 flex items-center justify-center rounded-xl border transition-all duration-200 flex-shrink-0 ${color.icon} ${color.text} hover:opacity-80`}
                      onClick={() => setEditingPlaylist(playlist.id)}
                      title="Editar videos"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-sky-600/15 hover:bg-sky-600/25 text-sky-400 border border-sky-600/30 transition-all duration-200 flex-shrink-0"
                      onClick={() => handleExport(playlist)}
                      title="Exportar playlist"
                    >
                      <Download size={14} />
                    </button>
                    <button
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-600/15 hover:bg-red-600/25 text-red-400 border border-red-600/30 transition-all duration-200 flex-shrink-0"
                      onClick={() => handleDelete(playlist.id)}
                      title="Eliminar playlist"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Schedule button */}
                  <button
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-transparent hover:bg-orange-600/10 text-orange-400 border border-orange-600/40 hover:border-orange-600/60 transition-all duration-200"
                    onClick={() => openScheduleModal(playlist)}
                  >
                    <Calendar size={14} />
                    Programar Horarios
                  </button>
                </div>
              );
            })}

            {/* Nueva Playlist card */}
            <div
              className="relative overflow-hidden transition-all duration-300 rounded-2xl border border-dashed border-black/15 dark:border-white/20 hover:border-accent-purple/40 p-5 flex flex-col items-center justify-center cursor-pointer min-h-[260px] group bg-transparent hover:bg-accent-purple/5"
              onClick={() => setShowModal(true)}
            >
              <div className="w-14 h-14 rounded-full border-2 border-black/15 dark:border-white/20 group-hover:border-accent-purple/50 flex items-center justify-center mb-4 transition-all duration-300">
                <Plus size={24} className="text-gray-400 group-hover:text-accent-purple transition-colors" />
              </div>
              <h3 className="text-base font-semibold text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors mb-1">
                Nueva Playlist
              </h3>
              <p className="text-xs text-gray-500 text-center">Crea un nuevo flujo de contenido 24/7</p>
            </div>
          </div>
        )}

        {/* ── TIMELINE TAB ──────────────────────────────────────────────────── */}
        {activeTab === 'timeline' && (
          <TimelineView playlists={playlists} channels={channels} />
        )}

        {/* ── CALENDAR TAB ────────────────────────────────────────────────── */}
        {activeTab === 'calendar' && (
          <PlaylistCalendar playlists={playlists} />
        )}

        {/* ── CREATE MODAL ──────────────────────────────────────────────────── */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in" onClick={() => setShowModal(false)}>
            <div className="glass-card max-w-md w-full mx-4 animate-slide-up" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 backdrop-blur-sm flex items-center justify-center border border-accent-purple/30">
                  <Plus className="text-accent-purple" size={24} />
                </div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-accent-purple bg-clip-text text-transparent">
                  Nueva Playlist
                </h2>
              </div>

              <form onSubmit={handleCreate}>
                <div className="mb-4">
                  <label className="block mb-2 font-semibold text-white">Nombre</label>
                  <input
                    type="text"
                    className="input-glass"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Mi Playlist 24/7"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-2 font-semibold text-white">Descripción</label>
                  <textarea
                    className="input-glass resize-none"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descripción de la playlist"
                    rows={3}
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-2 font-semibold text-white">Canal (Opcional)</label>
                  <select
                    className="input-glass"
                    value={formData.channel_id}
                    onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })}
                  >
                    <option value="">Sin canal asignado</option>
                    {channels.map((channel) => (
                      <option key={channel.id} value={channel.id}>{channel.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block mb-3 font-semibold text-white">Modo de Reproducción</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { val: false, label: 'En Orden', icon: <List size={18} />, gradient: 'from-accent-indigo/90 to-blue-500/90', shadow: 'shadow-accent-indigo/20', border: 'border-accent-indigo/30' },
                      { val: true,  label: 'Aleatorio', icon: <Shuffle size={18} />, gradient: 'from-accent-purple/90 to-purple-600/90', shadow: 'shadow-accent-purple/20', border: 'border-accent-purple/30' },
                    ].map(opt => (
                      <button
                        key={String(opt.val)}
                        type="button"
                        className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 border backdrop-blur-sm ${
                          formData.shuffle_mode === opt.val
                            ? `bg-gradient-to-r ${opt.gradient} text-white ${opt.border} shadow-lg ${opt.shadow}`
                            : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                        }`}
                        onClick={() => setFormData({ ...formData, shuffle_mode: opt.val })}
                      >
                        {opt.icon} {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-gray-400 mt-3 px-1">
                    {formData.shuffle_mode ? 'Los videos se reproducirán en orden aleatorio' : 'Los videos se reproducirán en el orden de la lista'}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button type="submit" className="btn-primary-gradient flex-1">Crear Playlist</button>
                  <button type="button" className="flex-1 px-6 py-3 rounded-lg font-semibold transition-all duration-200 bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 backdrop-blur-sm" onClick={() => setShowModal(false)}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── SCHEDULE MODAL ─────────────────────────────────────────────────── */}
        {showScheduleModal && schedulingPlaylist && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in" onClick={() => setShowScheduleModal(false)}>
            <div className="glass-card max-w-lg w-full mx-4 animate-slide-up" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-600/20 to-orange-700/20 backdrop-blur-sm flex items-center justify-center border border-orange-600/30">
                    <Calendar className="text-orange-400" size={24} />
                  </div>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-orange-400 bg-clip-text text-transparent">
                    Programar Horarios
                  </h2>
                </div>
                <button
                  className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 hover:text-white transition-all duration-200 flex items-center justify-center"
                  onClick={() => setShowScheduleModal(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mb-5 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-xs text-gray-400">
                Playlist: <span className="text-accent-purple font-semibold">{schedulingPlaylist.name}</span>
              </div>

              {!schedulingPlaylist.channel_id && scheduleData.days.length > 0 && (
                <div className="mb-5 flex items-start gap-3 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-300">Sin canal asignado</p>
                    <p className="text-xs text-red-400/80 mt-0.5">
                      Esta playlist no tiene canal asignado. El programador no podrá activarla aunque llegue la hora.
                      Asigna un canal desde el botón <span className="font-semibold">Editar</span> de la playlist antes de guardar el horario.
                    </p>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <label className="block mb-3 font-semibold text-white">Días de la semana</label>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {daysOfWeek.map((day) => (
                    <button
                      key={day.key}
                      type="button"
                      className={`px-3 py-2.5 rounded-lg text-xs font-semibold transition-all duration-200 border ${
                        scheduleData.days.includes(day.key)
                          ? 'bg-gradient-to-r from-orange-600/90 to-orange-500/90 text-white border-orange-500/30 shadow-lg shadow-orange-600/20'
                          : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                      }`}
                      onClick={() => toggleDay(day.key)}
                    >
                      {day.label.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block mb-2 font-semibold text-white">Hora de inicio</label>
                  <input type="time" className="input-glass" value={scheduleData.start_time} onChange={(e) => setScheduleData({ ...scheduleData, start_time: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-2 font-semibold text-white">Hora de fin</label>
                  <input type="time" className="input-glass" value={scheduleData.end_time} onChange={(e) => setScheduleData({ ...scheduleData, end_time: e.target.value })} />
                </div>
              </div>

              <div className="mb-5">
                <label className="flex items-center gap-3 cursor-pointer p-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-200 group">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-white/20 bg-white/5 text-accent-purple focus:ring-accent-purple/50 focus:ring-offset-0 cursor-pointer"
                    checked={scheduleData.loop}
                    onChange={(e) => setScheduleData({ ...scheduleData, loop: e.target.checked })}
                  />
                  <span className="text-white font-medium group-hover:text-orange-400 transition-colors">
                    Reproducir en loop durante el horario programado
                  </span>
                </label>
              </div>

              <div className="glass-card mb-6 border-orange-500/20 bg-orange-500/5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0 border border-orange-500/30">
                    <Clock size={16} className="text-orange-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white mb-1">Resumen de Programación</p>
                    <p className="text-sm text-gray-400">
                      Se reproducirá automáticamente{' '}
                      <span className="text-orange-400 font-semibold">
                        {scheduleData.days.length > 0 ? scheduleData.days.map(d => daysOfWeek.find(dw => dw.key === d)?.label.substring(0,3)).join(', ') : 'ningún día'}
                      </span>{' '}
                      de <span className="text-orange-400 font-semibold">{scheduleData.start_time}</span> a{' '}
                      <span className="text-orange-400 font-semibold">{scheduleData.end_time}</span>.
                      {scheduleData.loop && <span className="text-emerald-400 font-semibold"> Loop activo.</span>}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button type="button" className="btn-primary-gradient flex-1" onClick={handleSaveSchedule}>
                  Guardar Programación
                </button>
                <button type="button" className="flex-1 px-6 py-3 rounded-lg font-semibold bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-all duration-200" onClick={() => setShowScheduleModal(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Modal: Import Result */}
        {showImportResult && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-[200] animate-fade-in p-4"
               onClick={() => setShowImportResult(null)}>
            <div className="w-full max-w-md animate-slide-up rounded-2xl overflow-hidden shadow-2xl shadow-black/60 border border-white/10"
                 style={{ background: 'linear-gradient(145deg, #0f0f1a 0%, #13131f 100%)' }}
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-sky-600/20 border border-sky-600/30 flex items-center justify-center">
                    <FileJson size={18} className="text-sky-400" />
                  </div>
                  <h3 className="text-base font-semibold text-white">Playlist importada</h3>
                </div>
                <button onClick={() => setShowImportResult(null)}
                        className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors">
                  <X size={14} className="text-gray-400" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <p className="text-sm text-gray-300">
                  Se creó la playlist <span className="text-white font-semibold">"{showImportResult.name}"</span>
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="glass-card p-3 text-center">
                    <div className="text-2xl font-bold text-emerald-400">{showImportResult.matched}</div>
                    <div className="text-xs text-gray-400 mt-1">Videos vinculados</div>
                  </div>
                  <div className="glass-card p-3 text-center">
                    <div className="text-2xl font-bold text-orange-400">{showImportResult.unmatched}</div>
                    <div className="text-xs text-gray-400 mt-1">No encontrados</div>
                  </div>
                </div>
                {showImportResult.titles.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Videos no encontrados en este servidor:</p>
                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                      {showImportResult.titles.map((t, i) => (
                        <li key={i} className="text-xs text-orange-300 bg-orange-600/10 border border-orange-600/20 rounded-lg px-3 py-1.5 truncate">{t}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-gray-400 bg-white/5 rounded-lg px-3 py-2">
                  <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />
                  La playlist aparece en tu lista. Asígnale un canal para activarla.
                </div>
                <button className="w-full btn-primary-gradient" onClick={() => setShowImportResult(null)}>
                  Aceptar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Playlists;
